import { motion } from "framer-motion";
import { Store, Briefcase, ShoppingBag, GraduationCap, Stethoscope, Building2, HandCoins, Cpu } from "lucide-react";

const industries = [
  {
    icon: Store,
    title: "Local & Service Businesses",
    desc: "Contractors, clinics, restaurants, and studios that need local-SEO-ready sites and Google Business Profile mastery.",
    image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=1200&q=75",
    tint: "glass-tint-orange",
  },
  {
    icon: Cpu,
    title: "SaaS & B2B Tech",
    desc: "Product-led sites, comparison pages, and content clusters that rank for buyer-intent keywords and AI queries.",
    image: "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1200&q=75",
    tint: "glass-tint-blue",
  },
  {
    icon: ShoppingBag,
    title: "E-commerce & DTC",
    desc: "PDP redesigns, faceted-navigation SEO, and checkout optimization that lift revenue per session.",
    image: "https://images.unsplash.com/photo-1607083206869-4c7672e72a8a?auto=format&fit=crop&w=1200&q=75",
    tint: "glass-tint-pink",
  },
  {
    icon: Stethoscope,
    title: "Healthcare & Wellness",
    desc: "Trust-first design, HIPAA-safe forms, and E-E-A-T-strong content for YMYL topics that Google scrutinizes hardest.",
    image: "https://images.unsplash.com/photo-1631815588090-d4bfec5b1ccb?auto=format&fit=crop&w=1200&q=75",
    tint: "glass-tint-mint",
  },
  {
    icon: HandCoins,
    title: "Finance & Fintech",
    desc: "Landing pages, calculator tools, and authority content that survive AI search and compliance review.",
    image: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=1200&q=75",
    tint: "glass-tint-cyan",
  },
  {
    icon: GraduationCap,
    title: "Education & Training",
    desc: "Course landing pages, cohort funnels, and organic content that stays evergreen across cohorts.",
    image: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=1200&q=75",
    tint: "glass-tint-purple",
  },
  {
    icon: Building2,
    title: "Real Estate & Property",
    desc: "Listing sites, neighborhood pages, and local schema so buyers and sellers find you first.",
    image: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=1200&q=75",
    tint: "glass-tint-orange",
  },
  {
    icon: Briefcase,
    title: "Agencies & Studios",
    desc: "Portfolio sites, case study systems, and lead-magnet funnels for creative and consulting teams.",
    image: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1200&q=75",
    tint: "glass-tint-blue",
  },
];

export default function Industries() {
  return (
    <section id="industries" data-testid="industries-section" className="section-pad relative">
      <div className="container-apex">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="eyebrow mb-4"><span className="w-6 h-px bg-apex-cyan" /> Who we help</div>
            <h2 className="text-4xl md:text-5xl font-extrabold leading-[1.05]">
              Built for teams who need <span className="text-apex-orange">real growth</span>, not just a redesign.
            </h2>
          </div>
          <p className="text-white/70 max-w-md md:text-right">
            Whatever your category — local, SaaS, DTC, healthcare, finance — we bring the design, SEO, and AEO plays that work in your world.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {industries.map((it, i) => {
            const Icon = it.icon;
            return (
              <motion.div
                key={it.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.65, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -6 }}
                className={`${it.tint} glass-hover glass-inner-glow p-0 overflow-hidden relative group`}
                data-testid={`industry-card-${i}`}
              >
                <div className="relative h-36 overflow-hidden">
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-[1200ms] group-hover:scale-[1.08]"
                    style={{ backgroundImage: `url(${it.image})` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-apex-navy via-apex-navy/70 to-transparent" />
                  <div className="absolute bottom-3 left-3 inline-flex items-center gap-2 rounded-full bg-black/40 backdrop-blur-md border border-white/15 px-2.5 py-1.5">
                    <Icon className="w-3.5 h-3.5 text-apex-cyan" strokeWidth={1.75} />
                    <span className="text-[11px] font-bold uppercase tracking-widest text-white">Industry</span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-heading font-bold text-base leading-tight">{it.title}</h3>
                  <p className="mt-2 text-xs text-white/70 leading-relaxed">{it.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
